import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { fmt } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LayoutGrid, Store, Users, BarChart3, Palette, LogOut, Plus, Trash2, Ban, Check, CreditCard, Truck, Ticket, Gift, Building2, Eye, ExternalLink, Upload, Image as ImageIcon, Loader2, X, MapPin, Eye as EyeIcon, MousePointerClick } from "lucide-react";

export default function Backoffice() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [preview, setPreview] = useState(false);
  const [tab, setTab] = useState("menu");
  const isAdmin = user?.role === "tenant_admin";
  const loadOv = useCallback(() => api.get("/tenant/overview", { params: storeId ? { store_id: storeId } : {} })
    .then((r) => { setOv(r.data); setStoreId((prev) => prev || r.data.store?.id); }), [storeId]);
  useEffect(() => { loadOv(); }, [loadOv]);
  if (!ov) return <div className="min-h-screen flex items-center justify-center">Caricamento…</div>;
  const stores = ov.stores || [];
  const previewUrl = `/t/${ov.tenant.slug}${ov.store?.slug ? `/${ov.store.slug}` : ""}`;

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))]">
      <header className="bg-card border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><BrandLogo name={ov.tenant.commercial_name} size={30} color={ov.branding?.primary_color} /><Badge variant="secondary">{ov.plan?.name}</Badge></div>
          <div className="flex items-center gap-2">
            {stores.length > 1 && (
              <Select value={storeId || ""} onValueChange={setStoreId}>
                <SelectTrigger className="h-9 w-48" data-testid="store-switcher"><SelectValue placeholder="Sede" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.primary ? " ★" : ""}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setPreview(true)} data-testid="view-as-customer"><Eye size={16} className="mr-1" />Vedi come cliente</Button>
            <Button variant="outline" className="rounded-full" onClick={() => nav("/dashboard")} data-testid="go-dashboard">Cassa / Ordini</Button>
            <Button variant="ghost" onClick={() => { logout(); nav("/login"); }}><LogOut size={18} /></Button>
          </div>
        </div>
      </header>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center gap-2"><Eye size={18} /> Anteprima cliente — {ov.store?.name}</DialogTitle>
            <DialogDescription>Così vedono il tuo menu i clienti dal telefono.</DialogDescription>
          </DialogHeader>
          <div className="px-4 pb-4">
            <div className="mx-auto w-[340px] max-w-full h-[600px] rounded-[2.2rem] border-[10px] border-neutral-900 overflow-hidden bg-white shadow-2xl">
              <iframe title="Anteprima cliente" src={previewUrl} className="w-full h-full border-0" data-testid="customer-preview-iframe" />
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2">Puoi anche aggiungere prodotti al carrello e completare un ordine di prova.</p>
            <a href={previewUrl} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-1 text-sm brand-text font-medium" data-testid="preview-open-tab"><ExternalLink size={14} /> Apri in una nuova scheda</a>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-6xl mx-auto px-5 py-6">
        {isAdmin && <OnboardingChecklist ov={ov} storeId={storeId} goTab={setTab} />}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-5 flex-wrap h-auto">
            <TabsTrigger value="menu" data-testid="tab-menu"><LayoutGrid size={15} className="mr-1" />Menu</TabsTrigger>
            <TabsTrigger value="store" data-testid="tab-store"><Store size={15} className="mr-1" />Sede & Orari</TabsTrigger>
            {isAdmin && <TabsTrigger value="sedi" data-testid="tab-sedi"><Building2 size={15} className="mr-1" />Sedi</TabsTrigger>}
            <TabsTrigger value="staff" data-testid="tab-staff"><Users size={15} className="mr-1" />Staff</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 size={15} className="mr-1" />Statistiche</TabsTrigger>
            <TabsTrigger value="brand" data-testid="tab-brand"><Palette size={15} className="mr-1" />Brand</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing"><CreditCard size={15} className="mr-1" />Pagamenti</TabsTrigger>
            {ov.plan?.entitlements?.coupons && <TabsTrigger value="coupons" data-testid="tab-coupons"><Ticket size={15} className="mr-1" />Coupon</TabsTrigger>}
            {ov.plan?.entitlements?.coupons && <TabsTrigger value="proximity" data-testid="tab-proximity"><MapPin size={15} className="mr-1" />Prossimità</TabsTrigger>}
            {ov.plan?.entitlements?.loyalty && <TabsTrigger value="loyalty" data-testid="tab-loyalty"><Gift size={15} className="mr-1" />Loyalty</TabsTrigger>}
            {ov.plan?.entitlements?.delivery && <TabsTrigger value="delivery" data-testid="tab-delivery"><Truck size={15} className="mr-1" />Delivery</TabsTrigger>}
          </TabsList>
          <TabsContent value="menu"><MenuTab key={storeId} storeId={storeId} /></TabsContent>
          <TabsContent value="store"><StoreTab key={storeId} ov={ov} storeId={storeId} reload={loadOv} /></TabsContent>
          {isAdmin && <TabsContent value="sedi"><SediTab ov={ov} reload={loadOv} /></TabsContent>}
          <TabsContent value="staff"><StaffTab isAdmin={isAdmin} stores={ov.stores} /></TabsContent>
          <TabsContent value="stats"><StatsTab key={storeId} storeId={storeId} stores={ov.stores} /></TabsContent>
          <TabsContent value="brand"><BrandTab ov={ov} reload={loadOv} isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="billing"><BillingTab key={storeId} ov={ov} storeId={storeId} reload={loadOv} /></TabsContent>
          {ov.plan?.entitlements?.coupons && <TabsContent value="coupons"><CouponsTab isAdmin={isAdmin} /></TabsContent>}
          {ov.plan?.entitlements?.coupons && <TabsContent value="proximity"><ProximityTab isAdmin={isAdmin} storeId={storeId} /></TabsContent>}
          {ov.plan?.entitlements?.loyalty && <TabsContent value="loyalty"><LoyaltyTab ov={ov} reload={loadOv} isAdmin={isAdmin} /></TabsContent>}
          {ov.plan?.entitlements?.delivery && <TabsContent value="delivery"><DeliveryTab key={storeId} ov={ov} storeId={storeId} reload={loadOv} isAdmin={isAdmin} /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
function OnboardingChecklist({ ov, storeId, goTab }) {
  const [products, setProducts] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get("/tenant/menu", { params: storeId ? { store_id: storeId } : {} })
      .then((r) => setProducts((r.data.products || []).filter((p) => !p.archived).length))
      .catch(() => setProducts(0));
  }, [storeId]);
  if (products === null) return null;

  const items = [
    { key: "logo", label: "Aggiungi il logo del tuo locale", done: !!ov.branding?.logo_url, tab: "brand", icon: Palette },
    { key: "menu", label: "Crea il menu (almeno un prodotto)", done: products > 0, tab: "menu", icon: LayoutGrid },
    { key: "pay", label: "Configura i pagamenti", done: !!ov.payment_integration?.credentials_set, tab: "billing", icon: CreditCard },
  ];
  const doneCount = items.filter((i) => i.done).length;
  if (doneCount === items.length || dismissed) return null;

  return (
    <div className="mb-5 rounded-2xl border bg-card p-5" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-head text-lg font-bold">Completa la configurazione</h2>
          <p className="text-sm text-muted-foreground">Ancora pochi passi per iniziare a ricevere ordini · {doneCount}/{items.length} completati</p>
        </div>
        <button onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-foreground" data-testid="onboarding-dismiss"><X size={18} /></button>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-4">
        <div className="h-full bg-[#FFC400] transition-all" style={{ width: `${(doneCount / items.length) * 100}%` }} />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {items.map((it) => (
          <button key={it.key} onClick={() => !it.done && goTab(it.tab)} data-testid={`onboarding-${it.key}`}
            className={`flex items-center gap-3 text-left rounded-xl border p-3 transition-colors ${it.done ? "bg-[hsl(var(--success))]/5 border-[hsl(var(--success))]/30" : "hover:border-[#FFC400] hover:bg-[#FFC400]/5"}`}>
            <span className={`w-9 h-9 shrink-0 rounded-lg grid place-items-center ${it.done ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-[#FFC400]/15 text-[#c79400]"}`}>
              {it.done ? <Check size={18} /> : <it.icon size={17} />}
            </span>
            <span className="text-sm font-medium leading-tight">{it.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


}

function SediTab({ ov, reload }) {
  const [stores, setStores] = useState(ov.stores || []);
  const [f, setF] = useState({ name: "", slug: "", address: "", phone: "" });
  const load = useCallback(() => api.get("/tenant/stores").then((r) => setStores(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const maxStores = ov.plan?.entitlements?.max_stores || 1;
  const add = async () => {
    try { await api.post("/tenant/stores", f); toast.success("Sede creata"); setF({ name: "", slug: "", address: "", phone: "" }); load(); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (s) => { try { await api.delete(`/tenant/stores/${s.id}`); toast.success("Sede eliminata"); load(); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-card border rounded-2xl p-5">
        <p className="font-semibold mb-1">Nuova sede</p>
        <p className="text-xs text-muted-foreground mb-3">Sedi attive: {stores.length}/{maxStores}. Ogni sede ha menu, orari e ordini separati.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Nome sede" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sede-name" />
          <Input placeholder="Slug URL (es. centro)" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase() })} data-testid="sede-slug" />
          <Input placeholder="Indirizzo" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="sede-address" />
          <Input placeholder="Telefono" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="sede-phone" />
        </div>
        <Button className="brand-bg mt-3" onClick={add} data-testid="sede-create" disabled={stores.length >= maxStores}><Plus size={15} className="mr-1" />Crea sede</Button>
      </div>
      {stores.map((s) => (
        <div key={s.id} className="bg-card border rounded-xl p-4 flex items-center gap-3" data-testid={`sede-row-${s.slug}`}>
          <div className="w-10 h-10 rounded-xl brand-soft flex items-center justify-center"><Building2 className="brand-text" size={18} /></div>
          <div className="flex-1">
            <div className="font-semibold flex items-center gap-2">{s.name} {s.primary && <Badge variant="secondary">Principale</Badge>}</div>
            <p className="text-xs text-muted-foreground">/t/{ov.tenant.slug}/{s.slug} · {s.address || "—"}</p>
          </div>
          {!s.primary && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(s)} data-testid={`sede-del-${s.slug}`}><Trash2 size={14} /></Button>}
        </div>
      ))}
    </div>
  );
}

function MenuTab({ storeId }) {
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [prodDlg, setProdDlg] = useState(null);
  const [catName, setCatName] = useState("");
  const load = useCallback(() => api.get("/tenant/menu", { params: storeId ? { store_id: storeId } : {} }).then((r) => setMenu(r.data)), [storeId]);
  useEffect(() => { load(); }, [load]);

  const addCat = async () => { if (!catName) return; await api.post("/tenant/categories", { name: catName, sort: menu.categories.length, store_id: storeId }); setCatName(""); load(); };
  const toggleSold = async (p) => { await api.post(`/tenant/products/${p.id}/toggle-sold-out`); load(); };
  const archive = async (p) => { await api.delete(`/tenant/products/${p.id}`); load(); };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Nuova categoria" className="max-w-xs" data-testid="new-cat-name" />
        <Button variant="outline" onClick={addCat} data-testid="add-cat"><Plus size={15} className="mr-1" />Categoria</Button>
        <Button className="brand-bg hover:opacity-90 ml-auto" onClick={() => setProdDlg({})} data-testid="add-product"><Plus size={15} className="mr-1" />Prodotto</Button>
      </div>
      {menu.categories.map((c) => (
        <div key={c.id} className="mb-5">
          <h3 className="font-head font-bold mb-2">{c.name}</h3>
          <div className="space-y-2">
            {menu.products.filter((p) => p.category_id === c.id && !p.archived).map((p) => (
              <div key={p.id} className="bg-card border rounded-xl p-3 flex items-center gap-3" data-testid={`bo-product-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt="" className="w-11 h-11 rounded-md object-cover border shrink-0" />}
                <div className="flex-1">
                  <div className="font-semibold">{p.name} {p.sold_out && <Badge variant="secondary" className="ml-1">Esaurito</Badge>}</div>
                  <p className="text-xs text-muted-foreground">{fmt.eur(p.price)} · {p.option_groups?.length || 0} gruppi opzioni</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => toggleSold(p)} data-testid={`sold-${p.id}`}>{p.sold_out ? <Check size={14} /> : <Ban size={14} />}</Button>
                <Button size="sm" variant="ghost" onClick={() => setProdDlg(p)} data-testid={`edit-${p.id}`}>Modifica</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => archive(p)}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {prodDlg && <ProductEditor product={prodDlg} categories={menu.categories} storeId={storeId} onClose={() => setProdDlg(null)} onSaved={() => { setProdDlg(null); load(); }} />}
    </div>
  );
}

function ProductEditor({ product, categories, storeId, onClose, onSaved }) {
  const [f, setF] = useState({ name: product.name || "", category_id: product.category_id || categories[0]?.id, price: product.price || 0, description: product.description || "", allergens: (product.allergens || []).join(", "), labels: (product.labels || []).join(", "), images: (product.images && product.images.length) ? product.images : (product.image_url ? [product.image_url] : []) });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) { return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5MB)"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/tenant/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, images: [...prev.images, data.url] })); toast.success("Foto caricata");
    } catch (e2) { toast.error(apiErr(e2)); } finally { setUploading(false); if (e.target) e.target.value = ""; }
  };
  const save = async () => {
    const payload = { name: f.name, category_id: f.category_id, price: parseFloat(f.price), description: f.description, allergens: f.allergens.split(",").map((s) => s.trim()).filter(Boolean), labels: f.labels.split(",").map((s) => s.trim()).filter(Boolean), image_url: f.images[0] || null, images: f.images };
    try {
      if (product.id) await api.patch(`/tenant/products/${product.id}`, payload);
      else await api.post("/tenant/products", { ...payload, store_id: storeId });
      toast.success("Salvato"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{product.id ? "Modifica" : "Nuovo"} prodotto</DialogTitle><DialogDescription>Nome, prezzo, foto e opzioni del prodotto.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pe-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Categoria</Label>
              <Select value={f.category_id} onValueChange={(v) => set("category_id", v)}><SelectTrigger data-testid="pe-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Prezzo €</Label><Input type="number" step="0.5" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="pe-price" /></div>
          </div>
          <div><Label>Foto prodotto (galleria)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {f.images.map((im, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border" data-testid={`pe-image-${i}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("images", f.images.filter((_, j) => j !== i))} data-testid={`pe-image-remove-${i}`} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"><X size={12} /></button>
                  {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center">Copertina</span>}
                </div>
              ))}
              <label className="cursor-pointer w-16 h-16 rounded-lg border border-dashed flex items-center justify-center hover:bg-secondary">
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="pe-image-input" />
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} className="text-muted-foreground" />}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">La prima foto è la copertina. Senza foto ne mostriamo una automatica.</p>
          </div>
          <div><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div><Label>Allergeni (virgola)</Label><Input value={f.allergens} onChange={(e) => set("allergens", e.target.value)} /></div>
          <div><Label>Etichette (virgola)</Label><Input value={f.labels} onChange={(e) => set("labels", e.target.value)} placeholder="Novità, Piccante" /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button className="brand-bg" onClick={save} data-testid="pe-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StoreTab({ ov, storeId, reload }) {
  const [s, setS] = useState(ov.store);
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => { try { await api.patch("/tenant/store", s, { params: storeId ? { store_id: storeId } : {} }); toast.success("Impostazioni salvate"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="bg-card border rounded-2xl p-5 max-w-2xl space-y-4">
      <div className="flex items-center justify-between"><Label>Locale aperto</Label><Switch checked={s.open} onCheckedChange={(v) => set("open", v)} data-testid="st-open" /></div>
      <div className="flex items-center justify-between"><Label>Pausa ordini</Label><Switch checked={s.paused} onCheckedChange={(v) => set("paused", v)} data-testid="st-paused" /></div>
      {s.paused && <Input value={s.pause_message || ""} onChange={(e) => set("pause_message", e.target.value)} placeholder="Messaggio di pausa" />}
      <div className="flex items-center justify-between"><Label>Pagamento al ritiro</Label><Switch checked={s.pay_at_pickup} onCheckedChange={(v) => set("pay_at_pickup", v)} data-testid="st-payatpickup" /></div>
      <div className="flex items-center justify-between"><Label>Notifiche nuovi ordini (push)</Label><Switch checked={s.notify_new_order !== false} onCheckedChange={(v) => set("notify_new_order", v)} data-testid="st-notify-neworder" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Tempo prep. (min)</Label><Input type="number" value={s.prep_time} onChange={(e) => set("prep_time", +e.target.value)} /></div>
        <div><Label>Ordine minimo €</Label><Input type="number" step="0.5" value={s.min_order} onChange={(e) => set("min_order", +e.target.value)} /></div>
        <div><Label>Durata slot (min)</Label><Input type="number" value={s.slot_minutes} onChange={(e) => set("slot_minutes", +e.target.value)} /></div>
        <div><Label>Max ordini/slot</Label><Input type="number" value={s.max_orders_per_slot} onChange={(e) => set("max_orders_per_slot", +e.target.value)} data-testid="st-maxslot" /></div>
        <div><Label>Anticipo minimo (min)</Label><Input type="number" value={s.min_advance_minutes} onChange={(e) => set("min_advance_minutes", +e.target.value)} /></div>
        <div><Label>Giorni prenotabili</Label><Input type="number" value={s.max_days_ahead} onChange={(e) => set("max_days_ahead", +e.target.value)} /></div>
      </div>
      <div><Label>Tipo attività</Label>
        <Select value={s.business_type || "food"} onValueChange={(v) => set("business_type", v)}>
          <SelectTrigger data-testid="st-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="food">Food / Ristorazione</SelectItem>
            <SelectItem value="farmacia">Farmacia / Parafarmacia</SelectItem>
            <SelectItem value="sanitaria">Sanitaria</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground mt-1">Usato per il filtro "Tipo attività" nel portale clienti.</p>
      </div>
      <Button className="brand-bg" onClick={save} data-testid="st-save">Salva impostazioni</Button>
    </div>
  );
}

function StaffTab({ isAdmin, stores = [] }) {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "operator", store_id: "" });
  const load = useCallback(() => api.get("/tenant/staff").then((r) => setStaff(r.data)).catch(() => {}), []);
  useEffect(() => { if (isAdmin) load(); }, [load, isAdmin]);
  if (!isAdmin) return <p className="text-muted-foreground">Solo il titolare può gestire lo staff.</p>;
  const primaryId = (stores.find((s) => s.primary) || stores[0])?.id;
  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";
  const add = async () => { try { await api.post("/tenant/staff", { ...f, store_id: f.store_id || primaryId }); toast.success("Operatore creato"); setF({ name: "", email: "", password: "", role: "operator", store_id: "" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const changeStore = async (u, store_id) => { try { await api.patch(`/tenant/staff/${u.id}`, { store_id }); toast.success("Sede aggiornata"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-card border rounded-2xl p-5 grid grid-cols-2 gap-3">
        <Input placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name" />
        <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email" />
        <Input placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-pass" />
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="operator">Operatore</SelectItem><SelectItem value="store_manager">Responsabile sede</SelectItem><SelectItem value="driver">Driver</SelectItem></SelectContent></Select>
        {stores.length > 1 && (
          <Select value={f.store_id || primaryId || ""} onValueChange={(v) => setF({ ...f, store_id: v })}><SelectTrigger data-testid="staff-store"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
        )}
        <Button className="brand-bg col-span-2" onClick={add} data-testid="staff-add"><Plus size={15} className="mr-1" />Aggiungi</Button>
      </div>
      {staff.map((u) => (
        <div key={u.id} className="bg-card border rounded-xl p-3 flex justify-between items-center gap-2">
          <div className="flex-1"><p className="font-semibold">{u.name}</p><p className="text-xs text-muted-foreground">{u.email} · {u.role} · {storeName(u.store_ids?.[0])}</p></div>
          {stores.length > 1 && (
            <Select value={u.store_ids?.[0] || ""} onValueChange={(v) => changeStore(u, v)}><SelectTrigger className="h-8 w-40" data-testid={`staff-store-${u.email}`}><SelectValue placeholder="Sede" /></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          )}
          <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => { await api.delete(`/tenant/staff/${u.id}`); load(); }}><Trash2 size={14} /></Button>
        </div>
      ))}
    </div>
  );
}

function StatsTab({ storeId, stores = [] }) {
  const [s, setS] = useState(null);
  const [byStore, setByStore] = useState([]);
  useEffect(() => { api.get("/tenant/stats", { params: storeId ? { store_id: storeId } : {} }).then((r) => setS(r.data)); }, [storeId]);
  useEffect(() => { if ((stores || []).length > 1) api.get("/tenant/stats/by-store").then((r) => setByStore(r.data)).catch(() => {}); }, [stores]);
  if (!s) return <p>Caricamento…</p>;
  const cards = [["Oggi", s.day], ["Settimana", s.week], ["Mese", s.month]];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        {cards.map(([label, d]) => (
          <div key={label} className="bg-card border rounded-2xl p-5">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="font-head text-2xl font-extrabold mt-1">{fmt.eur(d.revenue)}</p>
            <p className="text-xs text-muted-foreground">{d.orders} ordini</p>
          </div>
        ))}
      </div>
      {byStore.length > 1 && (
        <div className="bg-card border rounded-2xl p-5">
          <p className="font-semibold mb-3">Confronto sedi (ultimi 30 giorni)</p>
          <div className="flex justify-between text-xs text-muted-foreground pb-1 border-b"><span>Sede</span><span>Oggi · Mese (ordini · incasso)</span></div>
          {byStore.map((st) => (
            <div key={st.store_id} className="flex justify-between text-sm py-1.5 border-b last:border-0" data-testid={`store-stat-${st.store_id}`}>
              <span className="font-medium">{st.name}{st.primary ? " ★" : ""}</span>
              <span className="text-muted-foreground">{st.day_orders}·{fmt.eur(st.day_revenue)} <span className="mx-1">|</span> <b className="text-foreground">{st.month_orders}·{fmt.eur(st.month_revenue)}</b></span>
            </div>
          ))}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border rounded-2xl p-5">
          <p className="font-semibold mb-3">Riepilogo</p>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span>Ticket medio</span><b>{fmt.eur(s.avg_ticket)}</b></div>
            <div className="flex justify-between"><span>Pagati online</span><b>{s.paid_online}</b></div>
            <div className="flex justify-between"><span>Pagati al ritiro</span><b>{s.paid_pickup}</b></div>
            <div className="flex justify-between"><span>Annullati/rifiutati</span><b>{s.cancelled}</b></div>
          </div>
        </div>
        <div className="bg-card border rounded-2xl p-5">
          <p className="font-semibold mb-3">Prodotti più venduti</p>
          {s.top_products.map((p) => <div key={p.name} className="flex justify-between text-sm py-0.5"><span>{p.name}</span><b>{p.qty}</b></div>)}
          {s.top_products.length === 0 && <p className="text-sm text-muted-foreground">Nessun dato</p>}
        </div>
        {s.coupons && s.coupons.length > 0 && (
          <div className="bg-card border rounded-2xl p-5 md:col-span-2">
            <p className="font-semibold mb-3">Coupon utilizzati</p>
            <div className="flex justify-between text-xs text-muted-foreground pb-1 border-b"><span>Codice</span><span>Ordini · Sconto · Fatturato</span></div>
            {s.coupons.map((c) => (<div key={c.code} className="flex justify-between text-sm py-1" data-testid={`stat-coupon-${c.code}`}><b>{c.code}</b><span>{c.orders} · {fmt.eur(c.discount)} · {fmt.eur(c.revenue)}</span></div>))}
          </div>
        )}
      </div>
    </div>
  );
}

function BrandTab({ ov, reload, isAdmin }) {
  const [b, setB] = useState(ov.branding);
  const [busy, setBusy] = useState(null);
  const canWL = ov.plan?.entitlements?.remove_branding;
  const upload = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5MB)"); e.target.value = ""; return; }
    setBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/tenant/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB((prev) => ({ ...prev, [kind === "logo" ? "logo_url" : "cover_url"]: data.url }));
      toast.success("Immagine caricata");
    } catch (e2) { toast.error(apiErr(e2)); } finally { setBusy(null); if (e.target) e.target.value = ""; }
  };
  const save = async () => {
    try { await api.patch("/tenant/branding", { primary_color: b.primary_color, branding_mode: b.branding_mode, logo_url: b.logo_url, cover_url: b.cover_url }); toast.success("Brand aggiornato"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!isAdmin) return <p className="text-muted-foreground">Solo il titolare può modificare il brand.</p>;
  return (
    <div className="bg-card border rounded-2xl p-5 max-w-xl space-y-5">
      <div><Label>Colore principale</Label><div className="flex gap-3 items-center mt-1"><Input type="color" value={b.primary_color} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="w-16 h-10 p-1" data-testid="brand-color" /><span className="text-sm">{b.primary_color}</span></div></div>

      <div>
        <Label>Logo del locale</Label>
        <div className="flex items-center gap-3 mt-1">
          <div className="w-16 h-16 rounded-lg border overflow-hidden bg-secondary grid place-items-center shrink-0">
            {b.logo_url ? <img src={b.logo_url} alt="logo" className="w-full h-full object-contain" data-testid="brand-logo-preview" /> : <span className="text-[10px] text-muted-foreground">Nessun logo</span>}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-2 h-9 px-3 rounded-md border hover:bg-secondary text-sm">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "logo")} data-testid="brand-logo-input" />
            {busy === "logo" ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Carica logo
          </label>
          {b.logo_url && <button onClick={() => setB({ ...b, logo_url: null })} className="text-xs text-muted-foreground hover:text-destructive" data-testid="brand-logo-remove">Rimuovi</button>}
        </div>
      </div>

      <div>
        <Label>Foto di copertina</Label>
        <div className="mt-1 space-y-2">
          <div className="w-full h-28 rounded-lg border overflow-hidden bg-secondary grid place-items-center">
            {b.cover_url ? <img src={b.cover_url} alt="cover" className="w-full h-full object-cover" data-testid="brand-cover-preview" /> : <span className="text-xs text-muted-foreground">Nessuna copertina</span>}
          </div>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 h-9 px-3 rounded-md border hover:bg-secondary text-sm">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "cover")} data-testid="brand-cover-input" />
              {busy === "cover" ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Carica copertina
            </label>
            {b.cover_url && <button onClick={() => setB({ ...b, cover_url: null })} className="text-xs text-muted-foreground hover:text-destructive" data-testid="brand-cover-remove">Rimuovi</button>}
          </div>
          <p className="text-[11px] text-muted-foreground">Logo e copertina appaiono sulla scheda del locale nel portale clienti.</p>
        </div>
      </div>

      <div><Label>Modalità branding</Label>
        <Select value={b.branding_mode} onValueChange={(v) => setB({ ...b, branding_mode: v })}><SelectTrigger data-testid="brand-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="standard">Pickfood Standard</SelectItem>
            <SelectItem value="custom">Personalizzato (Powered by)</SelectItem>
            <SelectItem value="white_label" disabled={!canWL}>Full White-Label {canWL ? "" : "(piano superiore)"}</SelectItem>
          </SelectContent></Select>
        {!canWL && <p className="text-xs text-muted-foreground mt-1">La rimozione del marchio richiede un piano superiore.</p>}
      </div>
      <Button className="brand-bg" onClick={save} data-testid="brand-save">Salva brand</Button>
    </div>
  );
}

function BillingTab({ ov, storeId, reload }) {
  const integ = ov.payment_integration || {};
  const [f, setF] = useState({ provider: integ.provider || "mock", mode: integ.mode || "sandbox", store_ref: integ.store_ref || "", secret_key: "" });
  const save = async () => { try { await api.patch("/tenant/payment-integration", f, { params: storeId ? { store_id: storeId } : {} }); toast.success("Integrazione aggiornata"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="bg-card border rounded-2xl p-5 max-w-xl space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        Le credenziali myPOS sono cifrate a riposo e non vengono mai mostrate in chiaro. Stato attuale: <b>{integ.credentials_set ? "configurate" : "MOCK sandbox"}</b>.
      </div>
      <div><Label>Provider</Label>
        <Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}><SelectTrigger data-testid="pi-provider"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="mock">Mock (sviluppo)</SelectItem><SelectItem value="mypos">myPOS</SelectItem></SelectContent></Select>
      </div>
      <div><Label>Modalità</Label>
        <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}><SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="sandbox">Sandbox</SelectItem><SelectItem value="production">Produzione</SelectItem></SelectContent></Select>
      </div>
      <div><Label>myPOS Store ref</Label><Input value={f.store_ref} onChange={(e) => setF({ ...f, store_ref: e.target.value })} /></div>
      <div><Label>Chiave segreta (write-only)</Label><Input type="password" value={f.secret_key} onChange={(e) => setF({ ...f, secret_key: e.target.value })} placeholder="Inserisci per aggiornare" data-testid="pi-secret" /></div>
      <Button className="brand-bg" onClick={save} data-testid="pi-save">Salva integrazione</Button>
    </div>
  );
}

function DeliveryTab({ ov, storeId, reload, isAdmin }) {
  const [enabled, setEnabled] = useState(ov.store.delivery_enabled || false);
  const [zones, setZones] = useState([]);
  const [z, setZ] = useState({ name: "", caps: "", fee: "", min_order: "", free_over: "" });
  const [tariff, setTariff] = useState({
    delivery_fee: ov.store.delivery_fee ?? "",
    delivery_free_over: ov.store.delivery_free_over ?? "",
    delivery_min_order: ov.store.delivery_min_order ?? "",
  });
  const [savingTariff, setSavingTariff] = useState(false);
  const sp = storeId ? { store_id: storeId } : {};
  const load = useCallback(() => api.get("/tenant/delivery/zones", { params: sp }).then((r) => setZones(r.data)), [storeId]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (v) => { setEnabled(v); await api.patch("/tenant/store", { delivery_enabled: v }, { params: sp }); toast.success(v ? "Consegna attiva" : "Consegna disattivata"); reload(); };
  const saveTariff = async () => {
    setSavingTariff(true);
    try {
      await api.patch("/tenant/store", {
        delivery_fee: tariff.delivery_fee === "" ? 0 : Number(tariff.delivery_fee),
        delivery_free_over: tariff.delivery_free_over === "" ? null : Number(tariff.delivery_free_over),
        delivery_min_order: tariff.delivery_min_order === "" ? 0 : Number(tariff.delivery_min_order),
      }, { params: sp });
      toast.success("Tariffa di consegna salvata"); reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingTariff(false); }
  };
  const addZone = async () => { try { await api.post("/tenant/delivery/zones", z, { params: sp }); toast.success("Zona creata"); setZ({ name: "", caps: "", fee: "", min_order: "", free_over: "" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { await api.delete(`/tenant/delivery/zones/${id}`); load(); };
  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-card border rounded-2xl p-5 flex items-center justify-between">
        <div><p className="font-semibold">Consegna a domicilio</p><p className="text-xs text-muted-foreground">Attiva il delivery per questa sede</p></div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="delivery-toggle" />
      </div>
      {isAdmin && (
        <div className="bg-card border rounded-2xl p-5">
          <p className="font-semibold mb-1">Tariffa di consegna standard</p>
          <p className="text-xs text-muted-foreground mb-3">La consegna la gestisci tu col tuo mezzo e accetti qualsiasi indirizzo. Questa tariffa si applica quando l'indirizzo non rientra in una zona CAP specifica. Lascia 0 per consegna gratuita.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Costo consegna €</Label><Input type="number" step="0.5" min="0" placeholder="0" value={tariff.delivery_fee} onChange={(e) => setTariff({ ...tariff, delivery_fee: e.target.value })} data-testid="tariff-fee" className="mt-1" /></div>
            <div><Label className="text-xs">Gratis oltre € (opz.)</Label><Input type="number" step="0.5" min="0" placeholder="es. 25" value={tariff.delivery_free_over} onChange={(e) => setTariff({ ...tariff, delivery_free_over: e.target.value })} data-testid="tariff-free-over" className="mt-1" /></div>
            <div><Label className="text-xs">Ordine minimo €</Label><Input type="number" step="0.5" min="0" placeholder="0" value={tariff.delivery_min_order} onChange={(e) => setTariff({ ...tariff, delivery_min_order: e.target.value })} data-testid="tariff-min" className="mt-1" /></div>
          </div>
          <Button className="brand-bg mt-3" onClick={saveTariff} disabled={savingTariff} data-testid="tariff-save">{savingTariff ? "Salvataggio…" : "Salva tariffa"}</Button>
        </div>
      )}
      {isAdmin && (
        <div className="bg-card border rounded-2xl p-5">
          <p className="font-semibold mb-3">Nuova zona (per CAP)</p>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Nome zona" value={z.name} onChange={(e) => setZ({ ...z, name: e.target.value })} data-testid="zone-name" />
            <Input placeholder="CAP separati da virgola" value={z.caps} onChange={(e) => setZ({ ...z, caps: e.target.value })} data-testid="zone-caps" />
            <Input placeholder="Costo €" type="number" step="0.5" value={z.fee} onChange={(e) => setZ({ ...z, fee: e.target.value })} data-testid="zone-fee" />
            <Input placeholder="Ordine minimo €" type="number" step="0.5" value={z.min_order} onChange={(e) => setZ({ ...z, min_order: e.target.value })} data-testid="zone-min" />
            <Input placeholder="Gratis oltre € (opz.)" type="number" step="0.5" value={z.free_over} onChange={(e) => setZ({ ...z, free_over: e.target.value })} />
          </div>
          <Button className="brand-bg mt-3" onClick={addZone} data-testid="zone-add"><Plus size={15} className="mr-1" />Aggiungi zona</Button>
        </div>
      )}
      {zones.map((zn) => (
        <div key={zn.id} className="bg-card border rounded-xl p-3 flex justify-between items-center">
          <div><p className="font-semibold">{zn.name}</p><p className="text-xs text-muted-foreground">CAP: {zn.caps.join(", ")} · {fmt.eur(zn.fee)} · min {fmt.eur(zn.min_order)}{zn.free_over ? ` · gratis oltre ${fmt.eur(zn.free_over)}` : ""}</p></div>
          {isAdmin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(zn.id)}><Trash2 size={14} /></Button>}
        </div>
      ))}
      <p className="text-xs text-muted-foreground">Aggiungi i corrieri dalla scheda <b>Staff</b> con ruolo «Driver».</p>
    </div>
  );
}

const COUPON_TYPES = { percentage: "Percentuale (%)", fixed: "Importo fisso (€)", free_delivery: "Consegna gratis" };

function CouponsTab({ isAdmin }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ code: "", type: "percentage", value: "", min_order: "", max_discount: "", expires_at: "", usage_limit: "" });
  const load = useCallback(() => api.get("/tenant/coupons").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const add = async () => {
    try {
      const payload = {
        code: f.code, type: f.type, value: parseFloat(f.value || 0),
        min_order: parseFloat(f.min_order || 0),
        max_discount: f.max_discount ? parseFloat(f.max_discount) : null,
        expires_at: f.expires_at ? new Date(f.expires_at + "T23:59:59").toISOString() : null,
        usage_limit: f.usage_limit ? parseInt(f.usage_limit) : null,
      };
      await api.post("/tenant/coupons", payload);
      toast.success("Coupon creato");
      setF({ code: "", type: "percentage", value: "", min_order: "", max_discount: "", expires_at: "", usage_limit: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (c) => { await api.patch(`/tenant/coupons/${c.id}`, { active: !c.active }); load(); };
  const del = async (id) => { await api.delete(`/tenant/coupons/${id}`); load(); };

  const valueLabel = (c) => c.type === "percentage" ? `-${c.value}%` : c.type === "fixed" ? `-${fmt.eur(c.value)}` : "Consegna gratis";

  return (
    <div className="max-w-3xl space-y-5">
      {isAdmin && (
        <div className="bg-card border rounded-2xl p-5">
          <p className="font-semibold mb-3">Nuovo codice sconto</p>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Codice (es. BENVENUTO10)" value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="coupon-code" />
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(COUPON_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            {f.type !== "free_delivery" && (
              <Input placeholder={f.type === "percentage" ? "Percentuale (1-100)" : "Importo € sconto"} type="number" step="0.5" value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="coupon-value" />
            )}
            {f.type === "percentage" && (
              <Input placeholder="Sconto max € (opz.)" type="number" step="0.5" value={f.max_discount} onChange={(e) => set("max_discount", e.target.value)} />
            )}
            <Input placeholder="Ordine minimo € (opz.)" type="number" step="0.5" value={f.min_order} onChange={(e) => set("min_order", e.target.value)} data-testid="coupon-min" />
            <Input placeholder="Limite utilizzi (opz.)" type="number" value={f.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} />
            <div><Label className="text-xs text-muted-foreground">Scadenza (opz.)</Label><Input type="date" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} /></div>
          </div>
          <Button className="brand-bg mt-3" onClick={add} data-testid="coupon-create"><Plus size={15} className="mr-1" />Crea coupon</Button>
        </div>
      )}
      {list.length === 0 && <p className="text-muted-foreground text-sm">Nessun coupon. {isAdmin ? "Crea il primo qui sopra." : ""}</p>}
      {list.map((c) => (
        <div key={c.id} className="bg-card border rounded-xl p-4 flex items-center gap-3" data-testid={`coupon-row-${c.code}`}>
          <div className="flex-1">
            <div className="font-head font-bold tracking-wide flex items-center gap-2">{c.code}{!c.active && <Badge variant="secondary">Disattivo</Badge>}</div>
            <p className="text-xs text-muted-foreground">
              {valueLabel(c)}{c.min_order ? ` · min ${fmt.eur(c.min_order)}` : ""}{c.max_discount ? ` · max ${fmt.eur(c.max_discount)}` : ""}
              {c.usage_limit ? ` · usi ${c.used_count || 0}/${c.usage_limit}` : ` · usi ${c.used_count || 0}`}
              {c.expires_at ? ` · scade ${new Date(c.expires_at).toLocaleDateString("it-IT")}` : ""}
            </p>
          </div>
          {isAdmin && <Button size="sm" variant="ghost" onClick={() => setEditing(c)} data-testid={`coupon-edit-${c.code}`}>Modifica</Button>}
          {isAdmin && <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`coupon-toggle-${c.code}`} />}
          {isAdmin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(c.id)} data-testid={`coupon-del-${c.code}`}><Trash2 size={14} /></Button>}
        </div>
      ))}
      {editing && <CouponEditor coupon={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CouponEditor({ coupon, onClose, onSaved }) {
  const [f, setF] = useState({
    type: coupon.type, value: coupon.value ?? "", min_order: coupon.min_order ?? "",
    max_discount: coupon.max_discount ?? "", usage_limit: coupon.usage_limit ?? "",
    expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 10) : "",
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    try {
      await api.patch(`/tenant/coupons/${coupon.id}`, {
        type: f.type, value: parseFloat(f.value || 0), min_order: parseFloat(f.min_order || 0),
        max_discount: f.max_discount ? parseFloat(f.max_discount) : null,
        usage_limit: f.usage_limit ? parseInt(f.usage_limit) : null,
        expires_at: f.expires_at ? new Date(f.expires_at + "T23:59:59").toISOString() : null,
      });
      toast.success("Coupon aggiornato"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifica {coupon.code}</DialogTitle><DialogDescription>Aggiorna tipo, valore e limiti del codice sconto.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Tipo</Label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}><SelectTrigger data-testid="coupon-edit-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(COUPON_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          </div>
          {f.type !== "free_delivery" && <div><Label>Valore</Label><Input type="number" step="0.5" value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="coupon-edit-value" /></div>}
          {f.type === "percentage" && <div><Label>Sconto max €</Label><Input type="number" step="0.5" value={f.max_discount} onChange={(e) => set("max_discount", e.target.value)} /></div>}
          <div><Label>Ordine minimo €</Label><Input type="number" step="0.5" value={f.min_order} onChange={(e) => set("min_order", e.target.value)} /></div>
          <div><Label>Limite utilizzi</Label><Input type="number" value={f.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} /></div>
          <div><Label>Scadenza</Label><Input type="date" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button className="brand-bg" onClick={save} data-testid="coupon-edit-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoyaltyTab({ ov, reload, isAdmin }) {
  const [s, setS] = useState({
    loyalty_enabled: ov.store.loyalty_enabled ?? false,
    loyalty_earn_rate: ov.store.loyalty_earn_rate ?? 1,
    loyalty_redeem_cost: ov.store.loyalty_redeem_cost ?? 100,
    loyalty_min_redeem: ov.store.loyalty_min_redeem ?? 100,
  });
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/tenant/loyalty/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => { try { await api.patch("/tenant/store", s); toast.success("Loyalty aggiornata"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-card border rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div><p className="font-semibold">Raccolta punti</p><p className="text-xs text-muted-foreground">I clienti accumulano punti per numero di telefono</p></div>
          <Switch checked={s.loyalty_enabled} onCheckedChange={(v) => set("loyalty_enabled", v)} data-testid="loyalty-enable" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><Label>Punti per €1 speso</Label><Input type="number" step="0.5" value={s.loyalty_earn_rate} onChange={(e) => set("loyalty_earn_rate", +e.target.value)} data-testid="loyalty-earn" /></div>
          <div><Label>Punti per €1 sconto</Label><Input type="number" value={s.loyalty_redeem_cost} onChange={(e) => set("loyalty_redeem_cost", +e.target.value)} data-testid="loyalty-cost" /></div>
          <div><Label>Punti min. riscatto</Label><Input type="number" value={s.loyalty_min_redeem} onChange={(e) => set("loyalty_min_redeem", +e.target.value)} data-testid="loyalty-min" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Esempio: con {s.loyalty_earn_rate} punto/€ e {s.loyalty_redeem_cost} punti = €1, un ordine da €20 vale {Math.floor(20 * s.loyalty_earn_rate)} punti.</p>
        {isAdmin ? <Button className="brand-bg" onClick={save} data-testid="loyalty-save">Salva impostazioni</Button> : <p className="text-xs text-muted-foreground">Solo il titolare può modificare.</p>}
      </div>
      <div className="bg-card border rounded-2xl p-5">
        <p className="font-semibold mb-3">Clienti con più punti</p>
        {customers.length === 0 && <p className="text-sm text-muted-foreground">Ancora nessun cliente con punti.</p>}
        {customers.map((c) => (<div key={c.phone} className="flex justify-between text-sm py-1 border-b last:border-0"><span>{c.phone}</span><b>{c.points} punti</b></div>))}
      </div>
    </div>
  );
}


const PROX_STATUS = { approved: "Approvata", pending: "In revisione", rejected: "Rifiutata", suspended: "Sospesa" };

function ProximityTab({ isAdmin, storeId }) {
  const [list, setList] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [f, setF] = useState({ coupon_id: "", headline: "", description: "", radius_km: "5", daily_cap: "", max_conversions: "", ends_at: "" });
  const load = useCallback(() => {
    api.get("/tenant/proximity/campaigns").then((r) => setList(r.data)).catch(() => {});
    api.get("/tenant/coupons").then((r) => setCoupons(r.data.filter((c) => c.active))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setF({ ...f, [k]: v });

  const add = async () => {
    if (!f.coupon_id) { toast.error("Seleziona un coupon da promuovere"); return; }
    if (!f.headline.trim()) { toast.error("Inserisci un titolo per l'offerta"); return; }
    try {
      await api.post("/tenant/proximity/campaigns", {
        coupon_id: f.coupon_id, headline: f.headline, description: f.description,
        radius_km: parseFloat(f.radius_km || 5),
        daily_cap: f.daily_cap ? parseInt(f.daily_cap) : null,
        max_conversions: f.max_conversions ? parseInt(f.max_conversions) : null,
        ends_at: f.ends_at ? new Date(f.ends_at + "T23:59:59").toISOString() : null,
      });
      toast.success("Campagna creata");
      setF({ coupon_id: "", headline: "", description: "", radius_km: "5", daily_cap: "", max_conversions: "", ends_at: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (c) => { await api.patch(`/tenant/proximity/campaigns/${c.id}`, { active: !c.active }); load(); };
  const del = async (id) => { await api.delete(`/tenant/proximity/campaigns/${id}`); load(); };

  return (
    <div className="max-w-3xl space-y-5">
      <div className="bg-card border rounded-2xl p-5">
        <p className="font-semibold flex items-center gap-2"><MapPin size={16} className="text-[#FFC400]" /> Offerte di prossimità</p>
        <p className="text-xs text-muted-foreground mt-1 mb-4">Promuovi un tuo coupon ai clienti che ordinano da altri locali Pickfood vicini (entro il raggio scelto). Compare a fine ordine, senza interrompere il checkout.</p>
        {isAdmin && (
          coupons.length === 0 ? (
            <p className="text-sm text-muted-foreground">Crea prima un coupon nella scheda «Coupon», poi potrai promuoverlo qui.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Coupon da promuovere</Label>
                <Select value={f.coupon_id} onValueChange={(v) => set("coupon_id", v)}>
                  <SelectTrigger data-testid="prox-coupon"><SelectValue placeholder="Scegli un coupon attivo" /></SelectTrigger>
                  <SelectContent>{coupons.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {COUPON_TYPES[c.type]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Titolo offerta</Label><Input placeholder="Es. -20% sul primo ordine da noi!" value={f.headline} onChange={(e) => set("headline", e.target.value)} data-testid="prox-headline" /></div>
              <div className="col-span-2"><Label className="text-xs">Descrizione (opz.)</Label><Textarea rows={2} placeholder="Dettagli dell'offerta" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="prox-desc" /></div>
              <div><Label className="text-xs">Raggio (km, max 5)</Label><Input type="number" step="0.5" min="0.5" max="5" value={f.radius_km} onChange={(e) => set("radius_km", e.target.value)} data-testid="prox-radius" /></div>
              <div><Label className="text-xs">Scadenza (opz.)</Label><Input type="date" value={f.ends_at} onChange={(e) => set("ends_at", e.target.value)} /></div>
              <div><Label className="text-xs">Cap impressioni/giorno (opz.)</Label><Input type="number" value={f.daily_cap} onChange={(e) => set("daily_cap", e.target.value)} /></div>
              <div><Label className="text-xs">Max conversioni (opz.)</Label><Input type="number" value={f.max_conversions} onChange={(e) => set("max_conversions", e.target.value)} /></div>
              <div className="col-span-2"><Button className="brand-bg" onClick={add} data-testid="prox-create"><Plus size={15} className="mr-1" />Crea campagna</Button></div>
            </div>
          )
        )}
      </div>
      {list.length === 0 && <p className="text-muted-foreground text-sm">Nessuna campagna di prossimità.</p>}
      {list.map((c) => (
        <div key={c.id} className="bg-card border rounded-xl p-4" data-testid={`prox-row-${c.id}`}>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="font-semibold flex items-center gap-2">{c.headline}
                <Badge variant="secondary">{PROX_STATUS[c.status] || c.status}</Badge>
                {!c.active && <Badge variant="secondary">Disattiva</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Coupon <b>{c.coupon_code}</b> · raggio {c.radius_km} km{c.ends_at ? ` · scade ${new Date(c.ends_at).toLocaleDateString("it-IT")}` : ""}</p>
            </div>
            {isAdmin && <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`prox-toggle-${c.id}`} />}
            {isAdmin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(c.id)} data-testid={`prox-del-${c.id}`}><Trash2 size={14} /></Button>}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1" data-testid={`prox-views-${c.id}`}><EyeIcon size={13} /> {c.stats?.views || 0} viste</span>
            <span className="flex items-center gap-1"><MousePointerClick size={13} /> {c.stats?.clicks || 0} click</span>
            <span className="flex items-center gap-1"><Check size={13} /> {c.stats?.conversions || 0} conversioni</span>
          </div>
        </div>
      ))}
    </div>
  );
}
